import React, { useState, useRef, useEffect } from 'react';
import { Mail, Bell, Zap, Image, Paperclip, Variable, Upload, Info } from 'lucide-react';
import useMarketingAndNotificationStore from '@/stores/AdminStore/MarketingAndNotificationStore';
import { Audience } from '@/services/PagesServices/MarketingAndNotificationService';
import './ComposeNotification.css';

const ComposeNotification: React.FC = () => {
  const { audiences: storeAudiences } = useMarketingAndNotificationStore();

  const [notificationTitle, setNotificationTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationType, setNotificationType] = useState<'email' | 'in-app' | 'both'>('email');
  const [audiences, setAudiences] = useState<Audience[]>(storeAudiences || []);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync store data to local state
  useEffect(() => {
    if (storeAudiences) {
      setAudiences(storeAudiences);
    }
  }, [storeAudiences]);

  const handleAudienceToggle = (id: string) => {
    setAudiences(audiences.map(aud =>
      aud.id === id ? { ...aud, checked: !aud.checked } : aud
    ));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        alert('Only CSV files are supported');
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      if (!file.name.endsWith('.csv')) {
        alert('Only CSV files are supported');
        return;
      }
      setUploadedFile(file);
    }
  };

  const selectedCount = audiences.filter(a => a.checked).length;

  return (
    <div className="s-admin-marketing-compose-content">
      <h2 className="s-admin-marketing-section-title">Compose Notification</h2>
      <p className="s-admin-marketing-section-subtitle">Create and send notifications to your users</p>

      <div className="s-admin-marketing-compose-layout">
        <div className="left-panel">
          <div className="s-admin-marketing-compose-card">
            <h3 className="s-admin-marketing-card-title">Message Details</h3>

            <div className="s-admin-marketing-form-group">
              <label className="s-admin-marketing-form-label">Notification Title</label>
              <input
                type="text"
                className="s-admin-marketing-form-input"
                placeholder="e.g., New Course Available"
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
              />
            </div>

            <div className="s-admin-marketing-form-group">
              <label className="s-admin-marketing-form-label">Message</label>
              <textarea
                className="s-admin-marketing-form-textarea"
                placeholder="Write your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>

            <div className="s-admin-marketing-toolbar">
              <button className="s-admin-marketing-toolbar-btn" title="Add Image">
                <Image size={16} />
                <span>Image</span>
              </button>
              <button className="s-admin-marketing-toolbar-btn" title="Attach File">
                <Paperclip size={16} />
                <span>Attach</span>
              </button>
              <button className="s-admin-marketing-toolbar-btn" title="Add Variable">
                <Variable size={16} />
                <span>Variable</span>
              </button>
            </div>
          </div>

          <div className="s-admin-marketing-compose-card">
            <h3 className="s-admin-marketing-card-title">Delivery Settings</h3>

            <div className="s-admin-marketing-form-group">
              <label className="s-admin-marketing-form-label">Notification Type</label>
              <div className="s-admin-marketing-notification-types">
                <button
                  className={`s-admin-marketing-type-btn ${notificationType === 'email' ? 's-admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('email')}
                >
                  <Mail size={20} />
                  <span>Email</span>
                </button>
                <button
                  className={`s-admin-marketing-type-btn ${notificationType === 'in-app' ? 's-admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('in-app')}
                >
                  <Bell size={20} />
                  <span>In-App</span>
                </button>
                <button
                  className={`s-admin-marketing-type-btn ${notificationType === 'both' ? 's-admin-marketing-type-btn-active' : ''}`}
                  onClick={() => setNotificationType('both')}
                >
                  <Zap size={20} />
                  <span>Both</span>
                </button>
              </div>
            </div>

            <div className="s-admin-marketing-form-group">
              <label className="s-admin-marketing-form-label">Select Audience</label>
              <div className="s-admin-marketing-audience-list">
                {audiences.map(audience => (
                  <label key={audience.id} className="s-admin-marketing-audience-item">
                    <input
                      type="checkbox"
                      checked={audience.checked}
                      onChange={() => handleAudienceToggle(audience.id)}
                      className="s-admin-marketing-audience-checkbox"
                    />
                    <div className="s-admin-marketing-audience-info">
                      <span className="s-admin-marketing-audience-name">{audience.name}</span>
                      <span className="s-admin-marketing-audience-count">{audience.userCount} users</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="s-admin-marketing-form-group">
              <div
                className="s-admin-marketing-upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="s-admin-marketing-upload-icon">
                  <Upload size={24} />
                </div>
                <p className="s-admin-marketing-upload-text">
                  {uploadedFile ? uploadedFile.name : 'Drop your CSV file here'}
                </p>
                <p className="s-admin-marketing-upload-subtext">
                  or click to browse from your computer
                </p>
                <button className="s-admin-marketing-upload-btn" onClick={(e) => e.stopPropagation()}>
                  Select File
                </button>
                <p className="s-admin-marketing-upload-hint">Supports .csv files up to 10MB</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            <div className="s-admin-marketing-form-row">
              <div className="s-admin-marketing-form-group s-admin-marketing-form-group-half">
                <label className="s-admin-marketing-form-label">Schedule</label>
                <select
                  className="s-admin-marketing-form-select"
                  value={scheduleType}
                  onChange={(e) => setScheduleType(e.target.value)}
                >
                  <option value="now">Send Now</option>
                  <option value="schedule">Schedule</option>
                </select>
              </div>
              <div className="s-admin-marketing-form-group s-admin-marketing-form-group-half">
                <label className="s-admin-marketing-form-label">Date & Time (Optional)</label>
                <input
                  type="date"
                  className="s-admin-marketing-form-input"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  disabled={scheduleType === 'now'}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="right-panel">
          <div className="s-admin-marketing-preview-card">
            <div className="s-admin-marketing-preview-header">
              <Mail size={16} className="s-admin-marketing-preview-icon" />
              <span>Email Preview</span>
            </div>

            <div className="s-admin-marketing-preview-content">
              <div className="s-admin-marketing-preview-from">
                <span className="s-admin-marketing-preview-label">From:</span>
                <span>noreply@company.com</span>
              </div>
              <div className="s-admin-marketing-preview-to">
                <span className="s-admin-marketing-preview-label">To:</span>
                <span>Select audience</span>
              </div>

              <div className="s-admin-marketing-preview-message">
                <h3 className="s-admin-marketing-preview-title">
                  {notificationTitle || 'Your notification title'}
                </h3>
                <p className="s-admin-marketing-preview-text">
                  {message || 'Your message will appear here...'}
                </p>
              </div>
            </div>

            <div className="s-admin-marketing-ready-banner">
              <Info size={16} />
              <div className="s-admin-marketing-ready-content">
                <span className="s-admin-marketing-ready-title">Ready to Send</span>
                <ul className="s-admin-marketing-ready-list">
                  <li>• {selectedCount} audience{selectedCount !== 1 ? 's' : ''} selected</li>
                  <li>• Type: {notificationType}</li>
                  <li>• Send immediately</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComposeNotification;
